// supervisor-attempt-directory-reader.ts
//
// Split out of supervisor-dispatch-guards.ts (code-reviewer-260910-p13-bc.md
// must-fix #1 — that file was 245 lines, over the 200-line/file budget).
// Pure read-only helpers over `<control_root>/attempts/` — no §18 gating
// logic here, just directory scans. Re-exported from
// supervisor-dispatch-guards.ts so every existing import path
// (`./supervisor-dispatch-guards.js`) keeps compiling unchanged.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { openMissionStoreReadOnly } from "./mission-store-read-only.js";

/**
 * Attempt IDs with "no closed receipt" (code-reviewer-260909-p8-m4.md H2):
 * a ticket still at status "in_progress" in the mission projection has a
 * worker dispatched for it that hasn't reached a terminal outcome, so ANY
 * attempt directory under `<control_root>/attempts/<ticketId>-<n>/` for
 * that ticket is open — attemptId format is `${ticket.id}-${attemptNo}`
 * (supervisor-attempt-cycle.ts). A "done"/terminal ticket's attempt dirs
 * are excluded even if still present on disk (closed, just not yet pruned).
 * Read-only — 06h R2 (dogfood 7 F-39): lock-free `openMissionStoreReadOnly`,
 * never the writer store. `doctor` after `kill -9` used to die here on the
 * orphaned `locks/journal.lock` (JournalLeaseError) before printing a line;
 * `run` startup (supervisor.ts) shares this reader and reclaims the lease
 * itself later, through the writer path.
 */
export async function readOpenAttemptIds(controlRoot: string): Promise<string[]> {
  const store = await openMissionStoreReadOnly(controlRoot);
  let inProgressTicketIds: string[];
  try {
    inProgressTicketIds = Object.entries(store.state.tickets)
      .filter(([, status]) => status === "in_progress")
      .map(([ticketId]) => ticketId);
  } finally {
    await store.close();
  }
  if (inProgressTicketIds.length === 0) return [];

  const attemptsDir = join(controlRoot, "attempts");
  let attemptDirNames: string[];
  try {
    attemptDirNames = await fs.readdir(attemptsDir);
  } catch {
    return []; // No attempts/ directory yet — nothing to protect.
  }

  return attemptDirNames.filter((name) =>
    inProgressTicketIds.some((ticketId) => name.startsWith(`${ticketId}-`)),
  );
}

/**
 * ISO timestamps for `max_sessions_per_hour`'s sliding window
 * (evaluateSessionCap) — EVERY attempt directory's mtime, open or already
 * closed (a finished attempt still used up a session slot in that hour).
 *
 * BUG-3 fix (dogfood 260925 luồng 5): `attempts/<attemptId>/` used to be
 * created ONLY by attempt-forensics.ts on contract_error/recovering, so a
 * clean dispatch left no directory/timestamp and `max_sessions_per_hour`
 * never actually blocked anything (KNOWN LIMITATION, code-reviewer-260909-
 * p8-m4.md unresolved #1). `journalAttemptStart`
 * (supervisor-loop-attempt-start.ts) now eagerly creates this directory on
 * EVERY dispatch, before the worker spawns — mtime is set at that moment, so
 * this reader counts every session, not just failed ones. The journal itself
 * still carries no per-event wall-clock field; this directory IS the durable
 * timestamp source, chosen because it already existed and required no
 * journal/schema change (KISS).
 */
export async function readAttemptTimestampsIso(controlRoot: string): Promise<string[]> {
  const attemptsDir = join(controlRoot, "attempts");
  let attemptDirNames: string[];
  try {
    attemptDirNames = await fs.readdir(attemptsDir);
  } catch {
    return [];
  }
  const timestamps: string[] = [];
  for (const name of attemptDirNames) {
    try {
      const st = await fs.stat(join(attemptsDir, name));
      timestamps.push(new Date(st.mtimeMs).toISOString());
    } catch {
      // Attempt dir removed between readdir and stat (retention race) — skip it.
    }
  }
  return timestamps;
}

/**
 * Req 9 (phase 05, NEW-3 fix after review H1): the LATEST `attempts/<ticketId>-<n>/`
 * mtime for one ticket — used by the orphan reconciler to decide whether an
 * `unlock-killed-worker.json` marker is newer than THIS ticket's own most
 * recent `attempt_started` (same durable-timestamp source as
 * `readAttemptTimestampsIso` above; the journal itself carries no wall-clock
 * field). A ticket can have several attempt dirs (retries) — only the
 * newest matters: an older attempt's dir being older than the marker says
 * nothing about whether the CURRENT attempt was the one unlock killed.
 * `undefined` when no attempt dir exists yet for the ticket — the caller
 * must then stay neutral (never guess which attempt, if any, a mission-wide
 * marker refers to).
 */
export async function readTicketLatestAttemptStartedAtIso(
  controlRoot: string,
  ticketId: string,
): Promise<string | undefined> {
  const attemptsDir = join(controlRoot, "attempts");
  let attemptDirNames: string[];
  try {
    attemptDirNames = await fs.readdir(attemptsDir);
  } catch {
    return undefined;
  }
  const prefix = `${ticketId}-`;
  let latestMs: number | undefined;
  for (const name of attemptDirNames) {
    if (!name.startsWith(prefix)) continue;
    try {
      const st = await fs.stat(join(attemptsDir, name));
      if (latestMs === undefined || st.mtimeMs > latestMs) latestMs = st.mtimeMs;
    } catch {
      // Attempt dir removed between readdir and stat (retention race) — skip it.
    }
  }
  return latestMs === undefined ? undefined : new Date(latestMs).toISOString();
}
